import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
} from 'react';

import { cx } from './internal/cx.js';

export type TextareaProps = Omit<
  ComponentPropsWithoutRef<'textarea'>,
  'onChange' | 'value' | 'defaultValue'
> & {
  value: string;
  onChange: (value: string) => void;
  /**
   * Grow to fit the content instead of scrolling. Sized from `scrollHeight` so it works in
   * browsers without CSS `field-sizing` (Safari, Firefox); `field-sizing: content` still applies
   * where supported.
   */
  autoGrow?: boolean;
};

function fitToContent(textarea: HTMLTextAreaElement): void {
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
}

/** Controlled native textarea styled like TextInput. Requires `@import '@nrx/ui/theme.css'`. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { value, onChange, className, autoGrow = false, ...props },
  ref,
): React.JSX.Element {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null): void => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  // Also re-measures when the value changes from outside (a reset, "Update from bank").
  useLayoutEffect(() => {
    if (autoGrow && innerRef.current) fitToContent(innerRef.current);
  }, [autoGrow, value]);

  // A narrower box wraps to more lines, so re-measure when the width changes (resize, side panel).
  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!autoGrow || !node || typeof ResizeObserver === 'undefined') return;
    let width = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth === width) return;
      width = node.clientWidth;
      fitToContent(node);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoGrow]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    if (autoGrow) fitToContent(event.target);
    onChange(event.target.value);
  };

  return (
    <textarea
      ref={setRefs}
      value={value}
      className={cx('input', 'textarea', autoGrow && 'textarea--auto-grow', className)}
      onChange={handleChange}
      {...props}
    />
  );
});
